/* Maison Sabelle homepage hero.
   - Sizes the poster to the space under the expanded header, and pins it under the visible header bar.
   - Writes the script on load and starts the film once the page has painted.
   - As the next section slides over, the poster gently recedes (slight zoom, dim, masthead lifts).
   Scroll is read from element positions, so it works whether Horizon scrolls the window or .page-wrapper. */
(() => {
  const clamp = (v, a = 0, b = 1) => Math.min(b, Math.max(a, v));
  const easeOut = (t) => 1 - Math.pow(1 - t, 3);
  const root = document.documentElement;

  const headerSection = () => document.querySelector('.sbh-section') || document.querySelector('#header-group');
  const headerBar = () => document.querySelector('.sbh-bar') || document.querySelector('.sbh') || headerSection();

  function init(hero) {
    if (!hero || hero.dataset.sbxReady) return;
    hero.dataset.sbxReady = '1';
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mqMobile = window.matchMedia('(max-width: 749px)');
    const wrapper = hero.closest('.shopify-section') || hero;
    const media = hero.querySelector('[data-media]');
    const dim = hero.querySelector('[data-dim]');
    const mast = hero.querySelector('[data-mast]');
    const cta = hero.querySelector('[data-cta]');
    const scriptSpan = hero.querySelector('[data-script] span');
    const video = hero.querySelector('[data-film]');

    /* Poster height: the space below the header as it first appears. */
    const size = () => {
      const hs = headerSection();
      const reserve = hs ? hs.getBoundingClientRect().height : 0;
      const h = Math.max(520, window.innerHeight - reserve - (mqMobile.matches ? 12 : 18));
      root.style.setProperty('--sbx-h', `${Math.round(h)}px`);
    };

    /* The script writes itself shortly after load. */
    if (scriptSpan && !reduce) {
      scriptSpan.style.setProperty('--rv', '0');
      const t0 = performance.now() + 900;
      const dur = 2300;
      const step = (t) => {
        const k = clamp((t - t0) / dur);
        scriptSpan.style.setProperty('--rv', (k <= 0 ? 0 : easeOut(k)).toFixed(4));
        if (k < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }

    /* Film: attach after load, play only while visible. Reduced motion keeps the still poster. */
    if (video && !reduce) {
      const start = () => {
        const src = mqMobile.matches ? video.dataset.srcMobile || video.dataset.src : video.dataset.src;
        if (!src) return;
        if (video.getAttribute('src') !== src) video.setAttribute('src', src);
        const p = video.play();
        if (p && p.catch) p.catch(() => {});
      };
      video.addEventListener('playing', () => video.classList.add('is-playing'));
      const io = new IntersectionObserver(
        (entries) => entries.forEach((e) => (e.isIntersecting ? start() : video.pause())),
        { threshold: 0.01 }
      );
      const kick = () => io.observe(hero);
      if (document.readyState === 'complete') setTimeout(kick, 120);
      else window.addEventListener('load', () => setTimeout(kick, 120), { once: true });
      mqMobile.addEventListener('change', () => { if (!video.paused) start(); });
    }

    size();
    if (reduce) return;

    /* Pin under the visible header bar, and recede as the next section covers the poster. */
    let raf = 0;
    const update = () => {
      raf = 0;
      const bar = headerBar();
      const top = bar ? Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) : 0;
      root.style.setProperty('--sbx-top', `${top}px`);
      const next = wrapper.nextElementSibling;
      if (!next) return;
      const h = hero.getBoundingClientRect().height || 1;
      const covered = clamp((top + h - next.getBoundingClientRect().top) / h);
      const q = easeOut(covered);
      if (media) media.style.transform = `scale(${(1 + 0.06 * q).toFixed(4)})`;
      if (dim) dim.style.opacity = (0.45 * q).toFixed(3);
      if (mast) {
        mast.style.transform = `translateY(${(-36 * q).toFixed(1)}px)`;
        mast.style.opacity = (1 - clamp(q * 1.4)).toFixed(3);
      }
      if (cta) cta.style.opacity = (1 - clamp(q * 2)).toFixed(3);
    };
    const request = () => { if (!raf) raf = requestAnimationFrame(update); };
    document.addEventListener('scroll', request, { capture: true, passive: true });
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', () => { size(); request(); });
    update();
  }

  const boot = () => document.querySelectorAll('[data-sbx-hero]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
